import React from 'react';
import styled, { css } from 'styled-components';
import Button from '@zocdoc-frontend-common/components-button';
import ErrorMessage from '@zocdoc-frontend-common/components-errormessage';
import Form from '@zocdoc-frontend-common/components-form';
import {
    Radio,
    RadioGroup,
    RadioText,
} from '@zocdoc-frontend-common/components-radio';
import Textbox from '@zocdoc-frontend-common/components-textbox';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { red70, slate30 } from '@zocdoc-mezzanine/foundations-colors';
import { SexAssignedAtBirthTooltip } from 'src/Booking/components/PatientInfoForm/SexAssignedAtBirthTooltip';
import { elementNames, errorNames } from './constants';

const PatientInfoFormView = ({
    errors,
    handleBlur,
    handleChange,
    handleChangeSex,
    handleClickSubmit,
    handleFocus,
    highlightedFields,
    provider,
    values,
}) => {
    const providerNameInSentence =
        provider?.shortNameInSentence || 'the provider';

    // Trying to put this inline generally messes up the spacing, silly JSX
    const subheadingCopy = `To book your appointment, we need to verify a few things for ${providerNameInSentence}'s office.`;

    return (
        <PatientInfoForm onSubmit={handleClickSubmit}>
            <Section>
                <Heading>Tell us a bit about you.</Heading>
                <Subheading>{subheadingCopy}</Subheading>
            </Section>

            <Section>
                <SectionTitle>Name</SectionTitle>
                <InputRow>
                    <StyledTextbox
                        className="exclude-from-playback"
                        data-test="first-name"
                        data-uem-id="firstname"
                        name={elementNames.firstName}
                        invalid={Boolean(
                            highlightedFields[elementNames.firstName]
                        )}
                        value={values[elementNames.firstName]}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        placeholder="First"
                        tabIndex="0"
                    />

                    <StyledTextbox
                        className="exclude-from-playback"
                        data-test="last-name"
                        data-uem-id="lastname"
                        name={elementNames.lastName}
                        invalid={Boolean(
                            highlightedFields[elementNames.lastName]
                        )}
                        value={values[elementNames.lastName]}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        placeholder="Last"
                        tabIndex="0"
                    />
                </InputRow>

                {errors[errorNames.nameError] && (
                    <ErrorMessage data-test="name-error">
                        Please type a name.
                    </ErrorMessage>
                )}
            </Section>

            <Section>
                <SectionTitle>Date of birth</SectionTitle>
                <InputRow>
                    <StyledTextbox
                        className="exclude-from-playback"
                        data-test="birth-month"
                        data-uem-id="birth-month"
                        maxLength={2}
                        name={elementNames.birthMonth}
                        invalid={Boolean(
                            highlightedFields[elementNames.birthMonth]
                        )}
                        value={values[elementNames.birthMonth]}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        placeholder="MM"
                        tabIndex="0"
                        type="tel"
                        aria-label="month"
                        /* type="tel" is required for mobile browsers to show the numeric keyboard.
                         * type="number" also works but causes gross arrows to show up on desktop; see
                         * https://www.w3schools.com/howto/howto_css_hide_arrow_number.asp and
                         * https://stackoverflow.com/questions/3790935/can-i-hide-the-html5-number-input-s-spin-box
                         */
                    />
                    <StyledTextbox
                        className="exclude-from-playback"
                        data-test="birth-day"
                        data-uem-id="birth-day"
                        maxLength={2}
                        name={elementNames.birthDay}
                        invalid={Boolean(
                            highlightedFields[elementNames.birthDay]
                        )}
                        value={values[elementNames.birthDay]}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        placeholder="DD"
                        tabIndex="0"
                        type="tel"
                        aria-label="day"
                        /* type="tel" is required for mobile browsers to show the numeric keyboard.
                         * type="number" also works but causes gross arrows to show up on desktop; see
                         * https://www.w3schools.com/howto/howto_css_hide_arrow_number.asp and
                         * https://stackoverflow.com/questions/3790935/can-i-hide-the-html5-number-input-s-spin-box
                         */
                    />
                    <StyledTextbox
                        className="exclude-from-playback"
                        data-test="birth-year"
                        data-uem-id="birth-year"
                        maxLength={4}
                        name={elementNames.birthYear}
                        invalid={Boolean(
                            highlightedFields[elementNames.birthYear]
                        )}
                        value={values[elementNames.birthYear]}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        placeholder="YYYY"
                        tabIndex="0"
                        type="tel"
                        aria-label="year"
                        /* type="tel" is required for mobile browsers to show the numeric keyboard.
                         * type="number" also works but causes gross arrows to show up on desktop; see
                         * https://www.w3schools.com/howto/howto_css_hide_arrow_number.asp and
                         * https://stackoverflow.com/questions/3790935/can-i-hide-the-html5-number-input-s-spin-box
                         */
                    />
                </InputRow>

                {
                    /* Only show one of these errors at a time! */
                    errors[errorNames.birthDateError] ? (
                        <ErrorMessage data-test="birth-date-error-1">
                            Oops! Try a valid date.
                        </ErrorMessage>
                    ) : (
                        errors[errorNames.tooYoungError] && (
                            /* We used to show an intentionally vague message because of COPPA; see question 12 in
                             * https://www.ftc.gov/tips-advice/business-center/guidance/complying-coppa-frequently-asked-questions-0#A.%20General%20Questions.
                             * However, legal decided our interpretation of COPPA was too strict and we thought the
                             * user experience was poor for users who might fat-finger the wrong date and end up
                             * seeming younger than 18, so we decided to go with more specific copy here.
                             */
                            <ErrorMessage data-test="birth-date-error-2">
                                You must be 18 or older to book an appointment
                                on Zocdoc.
                                <div>
                                    If you are a parent or guardian booking for
                                    an individual under 18, enter your own
                                    information first. You’ll be prompted to
                                    enter the patient’s information at a later
                                    step.
                                </div>
                            </ErrorMessage>
                        )
                    )
                }
            </Section>

            <Section>
                <SectionTitle id="sex-title">
                    Sex assigned at birth
                    <SexAssignedAtBirthTooltip />
                </SectionTitle>
                <StyledRadioGroup
                    role="radiogroup"
                    aria-labelledby="sex-title"
                    className="exclude-from-playback"
                    data-test="patient-info-sex-picker"
                    data-has-error={Boolean(
                        highlightedFields[elementNames.sex]
                    )}
                    hasError={Boolean(highlightedFields[elementNames.sex])}
                    name="sex"
                    onChangeCallback={handleChangeSex}
                    orientation="horizontal"
                    selectedValue={values[elementNames.sex]}
                >
                    <StyledRadio
                        data-test="radio-male"
                        data-uem-id="radio-male"
                        text="Male"
                        value="male"
                    />

                    <StyledRadio
                        data-test="radio-female"
                        data-uem-id="radio-female"
                        text="Female"
                        value="female"
                    />
                </StyledRadioGroup>
                {errors[errorNames.sexError] && (
                    <ErrorMessage data-test="sex-error">
                        Please select a sex.
                    </ErrorMessage>
                )}
            </Section>

            <StyledButton data-test="submit-button" type="submit">
                Continue booking
            </StyledButton>
        </PatientInfoForm>
    );
};

const PatientInfoForm = styled(Form)`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin: auto;
    /* Set spacing between adjacent divs that are direct children */
    > div + div {
        margin-top: 24px;
    }

    /* Override FEC components to use Mezzanine colors */
    ${ErrorMessage} {
        color: ${red70};
    }
`;

const StyledRadio = styled(Radio)`
    width: 50%;
    border-right: solid 1px ${slate30};
`;

const StyledButton = styled(Button)`
    box-sizing: border-box;
    font: 16px/28px ${FontFamily.SharpSansSemibold};
    ${breakpoint.small} {
        font-size: 18px;
    }
    margin-top: 24px;
    margin-bottom: 28px;
    padding: 9px 15px 9px;
    width: 100%;
`;

const SectionTitle = styled.label`
    display: flex;
    font: 15px/24px ${FontFamily.SharpSansSemibold};
    margin-bottom: 4px;
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const Heading = styled.h1`
    font: 28px/40px ${FontFamily.SharpSansBold};
    ${breakpoint.small} {
        font-size: 32px;
    }
    margin-bottom: 12px;
`;

const Subheading = styled.h2`
    font: 15px/24px ${FontFamily.SharpSansMedium};
    ${breakpoint.small} {
        font-size: 16px;
    }
    margin-bottom: 4px;
`;

const StyledTextbox = styled(Textbox)`
    border-color: ${slate30};
    min-width: 0;
    ${(p) =>
        p.invalid &&
        css`
            border-color: ${red70};
            border-right-width: 1px !important;
        `};
`;

const InputRow = styled.div`
    display: flex;
    width: 100%;

    > div {
        flex-basis: 100%;
    }

    > div:not(:last-of-type) > input {
        border-right-width: 0px;
    }
`;

const StyledRadioGroup = styled(RadioGroup)`
    width: 100%;

    ${RadioText} {
        font: 15px/24px ${FontFamily.SharpSansSemibold};
    }

    div {
        border-color: ${(p) => (p.hasError ? red70 : slate30)};
    }

    label {
        border-right-color: ${slate30};
    }
`;

export default PatientInfoFormView;
